import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/auth";
import { PageHeader, Icon } from "@/components/kit";
import { avatarFor } from "@/data/demo";

const TONE = {
  danger: { c: "#F87171", bg: "rgba(239,68,68,.12)", b: "rgba(239,68,68,.28)" },
  warning: { c: "#FBBF24", bg: "rgba(245,158,11,.12)", b: "rgba(245,158,11,.28)" },
  intelligence: { c: "#C084FC", bg: "rgba(168,85,247,.12)", b: "rgba(168,85,247,.28)" },
};

export default function RetentionRadar() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/retention-radar").then((r) => setD(r.data)).catch((e) => console.error("retention:", e)); }, []);

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Client Retention Radar" icon="Radar" color="#F87171"
        subtitle="The relationships that deserve your attention — before they quietly slip away." />

      {!d ? <div className="text-slate-500 font-head">Scanning your client base…</div> : (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="radar-summary">
            <div className="card-mfx p-5"><div className="text-xs text-slate-400">Relationships flagged</div><div className="font-head font-extrabold text-3xl text-slate-50 mt-1">{d.total_flagged}</div></div>
            <div className="card-mfx p-5"><div className="text-xs text-slate-400">AUM linked to at-risk SIPs</div><div className="font-head font-extrabold text-3xl text-red-400 mt-1">₹{d.at_risk_amount} L</div></div>
            <div className="card-mfx p-5"><div className="text-xs text-slate-400">Segments to action</div><div className="font-head font-extrabold text-3xl text-amber-400 mt-1">{d.segments.length}</div></div>
          </div>

          {d.segments.length === 0 ? (
            <div className="card-mfx p-10 text-center">
              <Icon name="ShieldCheck" size={36} className="mx-auto mb-3 text-emerald-400" />
              <div className="font-head font-semibold text-slate-100 text-lg">Your book looks healthy</div>
              <p className="text-slate-400 text-sm mt-1 max-w-md mx-auto">No at-risk, stopped or compliance-gap clients right now. Keep logging interactions so MFDx can spot early warning signs for you.</p>
            </div>
          ) : (
            <div className="space-y-5" data-testid="radar-segments">
              {d.segments.map((s) => {
                const t = TONE[s.tone] || TONE.warning;
                return (
                  <div key={s.key} className="card-mfx p-5" data-testid={`radar-segment-${s.key}`}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: t.bg }}><Icon name={s.icon} size={19} style={{ color: t.c }} /></span>
                      <div className="min-w-0">
                        <div className="font-head font-semibold text-slate-100 flex items-center gap-2">{s.label}<span className="text-xs font-bold rounded-full px-2 py-0.5" style={{ background: t.bg, color: t.c }}>{s.clients.length}</span></div>
                        <p className="text-sm text-slate-400 mt-0.5">{s.reason}</p>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {s.clients.map((c) => (
                        <div key={c.id} className="flex items-center gap-3 rounded-xl border border-[#222938] p-3" data-testid={`radar-client-${c.id}`}>
                          <img src={avatarFor(c.name)} alt={c.name} className="w-9 h-9 rounded-full object-cover" loading="lazy" />
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-slate-100 truncate">{c.name}</div>
                            <div className="text-xs text-slate-500 truncate">{c.city} · {c.segment} · {c.aum}</div>
                          </div>
                          <button onClick={() => nav(s.path)} data-testid={`radar-action-${c.id}`}
                            className="ml-auto text-xs font-semibold rounded-lg px-2.5 py-1.5 shrink-0" style={{ background: t.bg, color: t.c }}>{s.action}</button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
